import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { InnerPageLayout, InnerPageHero, InnerCTA } from "./InnerPageLayout";
import { RenderSection } from "./SectionRenderer";
import { SOLUTIONS } from "../data/pages";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "../components/site/Seo";

export const SolutionPage = () => {
  const { slug } = useParams();
  const data = SOLUTIONS[slug];
  if (!data) return <Navigate to="/" replace />;

  const otherSolutions = Object.values(SOLUTIONS).filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <InnerPageLayout title={data.h1}>
      <Seo
        title={data.eyebrow}
        description={data.intro}
        path={`/solutions/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: data.eyebrow,
          description: data.intro,
          provider: { "@type": "Organization", name: "Techovarya" },
        }}
      />
      <InnerPageHero
        eyebrow={data.eyebrow}
        h1={data.h1}
        intro={data.intro}
        primaryCta={data.primaryCta || "Start a Conversation"}
        secondaryCta={data.secondaryCta}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Solutions" },
          { label: data.eyebrow },
        ]}
      />

      {data.sections.map((section, i) => (
        <RenderSection key={i} section={section} i={i} />
      ))}

      {/* Related solutions */}
      <section
        data-testid="related-solutions"
        className="relative bg-[#050505] border-t border-white/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-8 bg-[#FF5F1F]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
              Related Solutions
            </span>
          </div>
          <h2 className="font-headings text-3xl md:text-5xl font-black tracking-tight text-white mb-10">
            Continue Exploring
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {otherSolutions.map((s) => (
              <Link
                key={s.slug}
                to={`/solutions/${s.slug}`}
                data-testid={`related-${s.slug}`}
                className="group bg-[#0A0A0A] hover:bg-[#111] transition-colors p-6 md:p-8 flex flex-col gap-4"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F]">
                    {s.eyebrow}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-[#FF5F1F] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                </div>
                <h3 className="font-headings text-xl md:text-2xl font-bold text-white tracking-tight leading-tight">
                  {s.h1}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <InnerCTA
        heading={data.finalCtaHeading}
        copy={data.finalCtaCopy}
        cta={data.finalCta}
        testid="final-cta"
      />
    </InnerPageLayout>
  );
};
